/**
 * The pixel patterns of items lying on the floor, and the egg of a defeated
 * boss (lifted from the dungeon-floors prototype's `dungeon-markers.js`: its
 * potion, antidote and crystal grids, drawn as pixel meshes there; the rope,
 * orb, sword, armor and egg grids are new, in the same 12×12 style). Each
 * grid is a picture in symbols; the colours are the theme's
 * `markers.items.palette` by symbol. An item content record names its
 * pattern (`icon`, `itemIcons` in `@ab/content-schema`). `l` is the icon's
 * body colour and `q` its dark shade (a palette symbol, or the body colour
 * times `darkShade`). This is presentation data: placeholders drawn in code
 * until the item icon assets ship (sprites, slice 3 and later).
 */
import type { ItemIcon, Theme } from '@ab/content-schema';
import * as THREE from 'three';

type Symbol = keyof Theme['markers']['items']['palette'];

const grids = {
  potion: ['.....kk.....', '.....cc.....', '.....kg.....', '....kwwk....', '...kgwwgk...', '...kgllgk...', '..kgwllllk..', '..kgwllllk..', '..kgwlllqk..', '..kglllqqk..', '...kggggk...', '....kkkk....'],
  vial: ['.....kk.....', '.....cc.....', '....kggk....', '...kgwwgk...', '..kgllllgk..', '..kgllwlgk..', '..kgwwwlgk..', '..kgllwlgk..', '..kglllqgk..', '...kllqqk...', '....kkkk....', '....ssss....'],
  crystal: ['.....k......', '....khk.....', '...khllk....', '..khhllqk...', '..khhllqk...', '..khllqqk...', '...kllqk....', '...klqqk....', '....kqk.....', '.....k......', '....sss.....', '...sssss....'],
  orb: ['............', '....kkkk....', '...kwwllk...', '..kwwllllk..', '..kwlllllk..', '..klllllqk..', '..klllllqk..', '..kllllqqk..', '...kllqqk...', '....kkkk....', '...ssssss...', '............'],
  rope: ['............', '...kkkkkk...', '..kllllllk..', '.kllkkkkllk.', '.klk....klk.', '.klk....klk.', '.kllkkkkllk.', '..kllllllk..', '...kkkklk...', '.......klk..', '........k...', '...ssssss...'],
  sword: ['..........k.', '.........kwk', '........kwlk', '.......kwlk.', '......kwlk..', '.....kwlk...', '..k.kwlk....', '..kkklk.....', '...kck......', '..kckk......', '.kck........', '.kk.........'],
  armor: ['............', '..kkk..kkk..', '.klllkkkllk.', '.kllllllllk.', '..kllwwllk..', '..kllllllk..', '..kllkklk...', '..kllllllk..', '..kqqqqqqk..', '...kkkkkk...', '..ssssssss..', '............'],
  egg: ['............', '.....kk.....', '....kllk....', '...kllllk...', '...klmllk...', '..kllllmlk..', '..klmllllk..', '..klllmllk..', '..kllllllqk.', '...kllqqk...', '....kkkk....', '...ssssss...'],
} as const;

type Pattern = keyof typeof grids;
type Look = { readonly pattern: Pattern; readonly body: Symbol; readonly dark?: Symbol };

/** Each content icon, then the egg, as a pattern and its body colours. */
export const looks: Readonly<Record<ItemIcon | 'egg', Look>> = {
  red_potion: { pattern: 'potion', body: 'r' },
  blue_potion: { pattern: 'potion', body: 'b' },
  gold_potion: { pattern: 'potion', body: 'y' },
  green_vial: { pattern: 'vial', body: 'v' },
  rope: { pattern: 'rope', body: 't' },
  orb: { pattern: 'orb', body: 'o' },
  red_crystal: { pattern: 'crystal', body: 'p', dark: 'd' },
  blue_rock: { pattern: 'crystal', body: 'b', dark: 'n' },
  sword: { pattern: 'sword', body: 'x' },
  armor: { pattern: 'armor', body: 'c' },
  egg: { pattern: 'egg', body: 'e' },
};

/** The order of looks in the atlas: a look's column index. */
export const lookOrder = Object.keys(looks) as readonly (ItemIcon | 'egg')[];

/** Pattern size in pixels, a pixel's size in tiles (the prototype's .032), the billboard's centre height, a derived dark shade, the alpha cut. */
export const patternPresentation = { pixels: 12, pixel: 0.032, centreY: 0.255, darkShade: 0.56, alphaTest: 0.5 } as const;

/**
 * One RGBA8 texture with every look side by side (12 × 12 texels each),
 * coloured from the theme; nearest filtering, no mipmaps, hard alpha. Owned by
 * the floor that made it.
 */
export const patternAtlas = (theme: Theme): THREE.DataTexture => {
  const n = patternPresentation.pixels;
  const width = n * lookOrder.length;
  const data = new Uint8Array(width * n * 4);
  const palette = theme.markers.items.palette;
  /** sRGB bytes of a `#rrggbb`, times `k` (the texture is sRGB, as the theme's colours are). */
  const bytes = (hex: string, k = 1): number[] => [1, 3, 5].map((at) => Math.round(parseInt(hex.slice(at, at + 2), 16) * k));
  lookOrder.forEach((name, column) => {
    const look = looks[name];
    const colourOf = (mark: string): number[] | null => {
      if (mark === '.') return null;
      if (mark === 'l') return bytes(palette[look.body]);
      if (mark === 'q') return look.dark !== undefined ? bytes(palette[look.dark]) : bytes(palette[look.body], patternPresentation.darkShade);
      return bytes(palette[mark as Symbol]);
    };
    grids[look.pattern].forEach((line, row) => {
      Array.from({ length: line.length }, (_, col) => line.charAt(col)).forEach((mark, col) => {
        const colour = colourOf(mark);
        if (colour === null) return;
        // Row 0 is the top of the picture; texture row 0 is the bottom.
        data.set([...colour, 255], ((n - 1 - row) * width + column * n + col) * 4);
      });
    });
  });
  const texture = new THREE.DataTexture(data, width, n);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
};
